import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Navbar from '../components/Navbar';
import BulkEmailModal from '../components/BulkEmailModal';
import { ChevronLeft, Mail, CheckCircle, XCircle, AlertTriangle, Clock, Filter, RefreshCw, Send, Eye, X, Users } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const AdminEmailLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, failed: 0, skipped: 0, sent_last_24h: 0 });
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [detailLog, setDetailLog] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (eventTypeFilter) params.append('event_type', eventTypeFilter);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/email-logs?${params.toString()}`),
        axios.get(`${API}/admin/email-logs/stats`)
      ]);
      
      setLogs(logsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, eventTypeFilter, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSendReminders = async () => {
    try {
      const response = await axios.post(`${API}/admin/send-reminders`);
      toast.success(`Reminders sent: ${response.data.sent}, Skipped: ${response.data.skipped_duplicates}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to send reminders');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3" />
            Sent
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3" />
            Failed
          </span>
        );
      case 'skipped':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <AlertTriangle className="w-3 h-3" />
            Skipped
          </span>
        );
      case 'queued':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Clock className="w-3 h-3" />
            Queued
          </span>
        );
      default:
        return <span className="text-sm text-[#666666]">{status}</span>;
    }
  };

  const getEventTypeLabel = (eventType) => {
    switch (eventType) {
      case 'signup_created':
        return 'Signup Notification';
      case 'signup_approved':
        return 'Approval Confirmation';
      case 'shift_reminder':
        return 'Shift Reminder';
      case 'admin_bulk_email':
        return 'Bulk Email (Admin)';
      case 'partner_bulk_email':
        return 'Bulk Email (Partner)';
      default:
        return eventType;
    }
  };

  const isBulk = (log) => log.event_type === 'admin_bulk_email' || log.event_type === 'partner_bulk_email';

  // Escape closes the bulk-email detail modal (matches the affordance on other dialogs)
  useEffect(() => {
    if (!detailLog) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setDetailLog(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detailLog]);

  return (
    <div className="min-h-screen" data-testid="admin-email-logs">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link to="/admin" className="text-[#8B2E3C] hover:underline flex items-center gap-1 mb-4">
            <ChevronLeft className="w-4 h-4" />
            Back to Admin Dashboard
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-[#222222] mb-2">Email Logs</h1>
              <p className="text-base text-[#666666]">View all email notification activity</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={fetchData}
                className="btn-outline flex items-center gap-2"
                data-testid="refresh-logs"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={handleSendReminders}
                className="btn-outline flex items-center gap-2"
                data-testid="send-reminders"
              >
                <Mail className="w-4 h-4" />
                Send Reminders Now
              </button>
              <button
                onClick={() => setShowCompose(true)}
                className="btn-primary flex items-center gap-2"
                data-testid="compose-email-button"
                title="Compose a new email — pick recipients by filter or hand-pick individuals"
              >
                <Send className="w-4 h-4" />
                Compose Email
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="card bg-[#FBF4EA]">
            <div className="text-sm text-[#666666]">Total Emails</div>
            <div className="text-2xl font-bold text-[#222222]">{stats.total}</div>
          </div>
          <div className="card bg-green-50">
            <div className="text-sm text-green-600">Sent</div>
            <div className="text-2xl font-bold text-green-700">{stats.sent}</div>
          </div>
          <div className="card bg-red-50">
            <div className="text-sm text-red-600">Failed</div>
            <div className="text-2xl font-bold text-red-700">{stats.failed}</div>
          </div>
          <div className="card bg-yellow-50">
            <div className="text-sm text-yellow-600">Skipped</div>
            <div className="text-2xl font-bold text-yellow-700">{stats.skipped}</div>
          </div>
          <div className="card bg-blue-50">
            <div className="text-sm text-blue-600">Sent (24h)</div>
            <div className="text-2xl font-bold text-blue-700">{stats.sent_last_24h}</div>
          </div>
        </div>

        {/* Heads-up banner when many emails are being skipped */}
        {stats.skipped > 0 && stats.sent === 0 && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3" data-testid="email-disabled-banner">
            <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-900">
              <strong>Email notifications are currently disabled.</strong> All recent emails are logged as <em>Skipped</em> with the message "Email notifications disabled" — this is informational, not an error. To start sending real emails, go to{' '}
              <Link to="/admin" className="underline font-medium">Admin Dashboard → Settings → Email Notifications</Link> and enable them.
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="card mb-6" data-testid="email-log-filters">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-[#8B2E3C]" />
            <h3 className="font-bold text-[#222222]">Filters</h3>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input-field"
                data-testid="filter-status"
              >
                <option value="">All Statuses</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="skipped">Skipped</option>
                <option value="queued">Queued</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Event Type</label>
              <select
                value={eventTypeFilter}
                onChange={(e) => setEventTypeFilter(e.target.value)}
                className="input-field"
                data-testid="filter-event-type"
              >
                <option value="">All Types</option>
                <option value="signup_created">Signup Notification</option>
                <option value="signup_approved">Approval Confirmation</option>
                <option value="shift_reminder">Shift Reminder</option>
                <option value="admin_bulk_email">Bulk Email (Admin)</option>
                <option value="partner_bulk_email">Bulk Email (Partner)</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input-field"
                data-testid="filter-start-date"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input-field"
                data-testid="filter-end-date"
              />
            </div>
          </div>
        </div>

        {/* Logs Table */}
        {loading ? (
          <div className="card text-center py-12">
            <div className="text-[#666666]">Loading email logs...</div>
          </div>
        ) : logs.length === 0 ? (
          <div className="card text-center py-12">
            <Mail className="w-12 h-12 text-[#E9DDCF] mx-auto mb-4" />
            <div className="text-[#666666]">No email logs found</div>
            <p className="text-sm text-[#999999] mt-2">
              Email logs will appear here once notifications are sent
            </p>
          </div>
        ) : (
          <div className="card overflow-x-auto" data-testid="email-logs-table">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b-2 border-[#E9DDCF]">
                  <th className="text-left py-3 px-4 font-semibold">Date/Time</th>
                  <th className="text-left py-3 px-4 font-semibold">Event Type</th>
                  <th className="text-left py-3 px-4 font-semibold">Recipient</th>
                  <th className="text-left py-3 px-4 font-semibold">Sent By</th>
                  <th className="text-left py-3 px-4 font-semibold">Opportunity / Subject</th>
                  <th className="text-center py-3 px-4 font-semibold">Status</th>
                  <th className="text-center py-3 px-4 font-semibold">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const bulk = isBulk(log);
                  return (
                  <tr key={log.id} className="border-b border-[#E9DDCF] hover:bg-[#FBF4EA]" data-testid={`email-log-row-${log.id}`}>
                    <td className="py-3 px-4 text-sm">
                      {log.created_at ? format(new Date(log.created_at), 'MMM d, yyyy h:mm a') : '-'}
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm font-medium">{getEventTypeLabel(log.event_type)}</span>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {bulk
                        ? (<span className="inline-flex items-center gap-1 text-[#8B2E3C]"><Users className="w-3.5 h-3.5" />{log.recipient_count ?? '?'} recipient{log.recipient_count === 1 ? '' : 's'}</span>)
                        : log.recipient_email}
                    </td>
                    <td className="py-3 px-4 text-sm text-[#666]">
                      {log.sent_by || '—'}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {bulk ? (log.subject || '—') : (log.opportunity_title || '-')}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {getStatusBadge(log.status)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {bulk ? (
                        <button
                          onClick={() => setDetailLog(log)}
                          className="text-[#8B2E3C] hover:underline text-sm flex items-center gap-1 mx-auto"
                          data-testid={`view-bulk-email-${log.id}`}
                          title="View message + recipient list"
                        >
                          <Eye className="w-4 h-4" /> View
                        </button>
                      ) : (
                        <span className={`text-sm max-w-xs truncate inline-block ${log.status === 'failed' ? 'text-red-600' : 'text-[#888]'}`} title={log.error_message || ''}>
                          {log.error_message || '-'}
                        </span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Compose new bulk email */}
      <BulkEmailModal
        isOpen={showCompose}
        onClose={() => { setShowCompose(false); fetchData(); }}
        scope="admin"
      />

      {/* Bulk email details */}
      {detailLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="bulk-email-detail-modal">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[92vh] overflow-y-auto">
            <div className="p-5 border-b border-[#E9DDCF] flex items-start justify-between sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-xl font-bold text-[#222]">Email details</h2>
                <p className="text-xs text-[#666] mt-0.5">{getEventTypeLabel(detailLog.event_type)}</p>
              </div>
              <button onClick={() => setDetailLog(null)} className="text-[#666] hover:text-[#222]" data-testid="bulk-email-detail-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3 text-[#444]">
                <div>
                  <div className="text-xs text-[#888]">Sent</div>
                  <div className="font-medium" data-testid="bulk-email-detail-date">{detailLog.created_at ? format(new Date(detailLog.created_at), 'MMM d, yyyy h:mm a') : '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-[#888]">Sent by</div>
                  <div className="font-medium" data-testid="bulk-email-detail-sent-by">{detailLog.sent_by || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-[#888]">Recipients</div>
                  <div className="font-medium" data-testid="bulk-email-detail-count">
                    {detailLog.recipient_count ?? '?'}
                    {detailLog.recipient_kind && (
                      <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-[#FBF4EA] text-[#8B2E3C] font-medium capitalize" data-testid="bulk-email-detail-kind">
                        {detailLog.recipient_kind === 'both' ? 'Participants + Partners' : detailLog.recipient_kind}
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-[#888]">Status</div>
                  <div>{getStatusBadge(detailLog.status)}</div>
                </div>
              </div>
              <div>
                <div className="text-xs text-[#888]">Subject</div>
                <div className="font-medium text-[#222]" data-testid="bulk-email-detail-subject">{detailLog.subject || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-[#888] mb-1">Message</div>
                {detailLog.body_html ? (
                  <div
                    className="bg-[#FBF4EA] border border-[#E9DDCF] rounded p-3 text-[#222] text-sm whitespace-pre-wrap break-words max-h-48 overflow-y-auto"
                    data-testid="bulk-email-detail-body"
                    dangerouslySetInnerHTML={{ __html: detailLog.body_html }}
                  />
                ) : (
                  <div className="text-[#888] text-sm">—</div>
                )}
              </div>
              <div>
                <div className="text-xs text-[#888] mb-1">Recipient list</div>
                {(detailLog.recipients_list || []).length === 0 ? (
                  <div className="text-[#888] text-sm">No recipient list captured.</div>
                ) : (
                  <ul className="bg-white border border-[#E9DDCF] rounded max-h-56 overflow-y-auto divide-y divide-[#F0E5D6]" data-testid="bulk-email-detail-recipients">
                    {detailLog.recipients_list.map((r, i) => (
                      <li key={`${r.email}-${i}`} className="px-3 py-1.5 text-sm flex items-center justify-between gap-2">
                        <span className="font-medium text-[#222] truncate">{r.name || r.email}</span>
                        {r.name && <span className="text-xs text-[#666] truncate">{r.email}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {detailLog.error_message && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                  <strong>Error:</strong> {detailLog.error_message}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEmailLogs;
